import { memo, useContext, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore, type KeyboardEvent, type MouseEvent, type MutableRefObject, type PointerEvent, type ReactElement, type ReactNode, type RefObject } from "react";
import { minimapScale, type Size } from "odyssey-shared";
import { Button, Glyph, GlyphButton, ListRow, SearchField, Window, KIT_METRICS, logical } from "../../ui/kit/index.ts";
import { choiceForKey } from "../../ui/kit/state.ts";
import { say } from "../../words.ts";
import type { HudArea } from "../hud/slots.ts";
import { FieldCanvas, Labels, Legend, OtherNames, legendWords, type HudMinimap } from "../minimap/minimap.tsx";
import type { MinimapField } from "../minimap/store.ts";
import { PopOutButton } from "./popout-button.tsx";
import type { MapPopout } from "./popout-host.ts";
import { MAP_SCALE, pixelPoint, tilePoint, type EdgeMark, type TilePoint } from "./geometry.ts";
import { MARK_SIZES, markSize, nameBox } from "./labels.ts";
import { paintView, panelBox, worldMapLayout, type WorldMapLayout } from "./layout.ts";
import { shownLevel, type MapLevel } from "./levels.ts";
import { LEGEND_GAP, legendStrip, nearbyField, pinchStep, wheelSteps } from "./nearby.ts";
import { distanceWords, partyColourFor, type KnownPlace, type PlaceKind, type PlaceMark } from "./places.ts";
import { ROW_HEIGHT, scrollFor, scrollUnder, showsFilter, visibleItems, type PanelModel, type PanelScroll } from "./panel.ts";
import { FILTER_LENGTH, type WorldMapSnapshot, type WorldMapStore } from "./store.ts";
import { TravelContext, TravelMarks, TravelNote, TravelPanel, travelHeading, useTravel, type TravelControl } from "../travel/map-panel.tsx";

/**
 * The world map window (P2.12-T6 and P2.12-T19, ADR-100 decisions 1, 5 and 10 to 13, the amendment's decisions 26 and 27
 * and the note of 2026-10-14; the design's "World map" card, a visual reference): a DOM frame with a canvas for its
 * contents, centred at 480 by 300 and the whole screen at the phone floor, the map filling its body. The canvas holds
 * the ground as the store's picture draws it; the marks are buttons in one layer over it that moves as one, each reached
 * by the pointer, a touch and the keyboard, and a name is drawn beside a mark only where the store's rule says. A press
 * that does not move drops the pin there, a drag moves the view, Centre (and C) returns it to the character, and the
 * arrow at the view's edge says "You are here" when a drag has taken the character out of view. Known places is a panel
 * over the map that a small Places button opens and shuts: its rows are grouped by kind, filtered by name once there are
 * many, and only the rows in view are drawn; a click, a tap, Space or Enter on a row pins the place. The window is not a
 * pause: its keys are the store's layers (`keys.ts`) and the game keeps the rest.
 *
 * The window has levels (P2.12-T21, ADR-100's amendment of 2026-10-14, decisions 39 to 42 and 44): it opens at Nearby,
 * the minimap's field across the body with its marks, names and legend (`minimap/minimap.tsx`), and zooms out to Region,
 * all of the above. The zoom buttons, the wheel over the window and a two finger pinch step the ladder; the level's name
 * is in the title bar beside the map's ("Aethervale · Nearby"), so nothing is drawn over the field or the Places panel (P2.12-T22). A zoom never shows an empty level (P2.12-T32, ADR-100's note under decision 42):
 * the level being left stays drawn, and keeps its picture, until the next has its first one (`shownLevel`).
 */

/**
 * What the window acts on: the store's reading and acting side.
 */
export type WorldMapControl = Pick<WorldMapStore, "subscribe" | "getSnapshot" | "onDraw" | "setView" | "close" | "centre" | "pan" | "placePin" | "pinPlace" | "clearPin" | "source" | "pictureSize" | "togglePanel" | "closePanel" | "setFilter" | "hoverMark" | "focusMark" | "lineOf" | "zoomIn" | "zoomOut" | "setPhone" | "float" | "dock" | "popOut" | "popIn" | "onBringForward">;

/**
 * What the HUD is given to draw the window.
 */
export interface HudWorldMap {
  readonly store: WorldMapControl;
  readonly popout?: MapPopout | undefined;
  readonly travel?: TravelControl | undefined;
}

export { MARK_SIZES };

/**
 * How far a press may move, in logical pixels, and still be a click that drops a pin.
 */
export const CLICK_SLOP = 3;

/**
 * How far an arrow key moves the view, in logical pixels.
 */
export const KEY_PAN = 16;

/**
 * Stops a pointer press from reaching the view under a control, so it neither drags nor drops a pin.
 *
 * @param event The press.
 */
function stopPress(event: PointerEvent<HTMLElement>): void {
  event.stopPropagation();
}

/**
 * Keeps a pointer press on a control from taking focus, so the game keeps its keys.
 *
 * @param event The press.
 */
function keepFocus(event: MouseEvent<HTMLElement>): void {
  event.preventDefault();
}

/**
 * A mark in the view: a button at a place, named for hover, focus and tap. Only a mark the store names is a tab stop,
 * so a map of many places is not many stops; any mark can still be focused by a click or a panel row.
 *
 * @param props The `kind` that draws it, its box `left`, `top`, `width` and `height`, its `name`, whether it is a
 *   tab stop, `hover` and `focus`, told when the pointer or focus enters or leaves it, and `onPress`.
 * @returns The button.
 */
function Mark({ kind, left, top, width, height, name, tabbable, hover, focus, onPress, colour, children }: { readonly kind: string; readonly colour?: string | undefined; readonly left: number; readonly top: number; readonly width: number; readonly height: number; readonly name: string; readonly tabbable: boolean; readonly hover: (on: boolean) => void; readonly focus: (on: boolean) => void; readonly onPress?: (() => void) | undefined; readonly children?: ReactNode }): ReactElement {
  return (
    <button
      type="button"
      className={`ot-worldmap__mark ot-worldmap__mark--${kind}`}
      data-worldmap="mark"
      data-mark={kind}
      data-colour={colour}
      aria-label={name}
      tabIndex={tabbable ? 0 : -1}
      style={{ left: logical(left), top: logical(top), width: logical(width), height: logical(height), ...(colour === undefined ? {} : { backgroundColor: `var(--ot-${colour})` }) }}
      onPointerDown={stopPress}
      onMouseDown={keepFocus}
      onMouseEnter={() => hover(true)}
      onMouseLeave={() => hover(false)}
      onFocus={() => focus(true)}
      onBlur={() => focus(false)}
      onClick={() => {
        hover(true);
        onPress?.();
      }}
    >
      {children}
    </button>
  );
}

/**
 * The colour swatch a party member's row leads with, or nothing for any other row or a party row without a valid place.
 *
 * @param entry The row's entry.
 * @returns The row's `lead` property, or an empty object.
 */
function swatchLead(entry: KnownPlace): { readonly lead?: { readonly node: ReactNode; readonly height: number } } {
  const colour = entry.kind === "party" ? partyColourFor(entry.key, entry.place) : null;
  return colour === null ? {} : { lead: { node: <span className="ot-worldmap__swatch" data-worldmap="swatch" data-colour={colour} style={{ backgroundColor: `var(--ot-${colour})` }} />, height: MARK_SIZES.party } };
}

/**
 * The places' marks and, over all of them, the names beside the ones the store names, in picture pixels so the layer
 * moves as one with the view and a step of the character changes none of them.
 *
 * @param props The `marks`, the keys `named`, the `map` to tell of the pointer and focus, and `onTip`, which shows a
 *   mark's words.
 * @returns The marks.
 */
function MarksLayerView({ marks, named, map, onTip }: { readonly marks: readonly PlaceMark[]; readonly named: readonly string[]; readonly map: WorldMapControl; readonly onTip: (tip: string | null) => void }): ReactElement {
  const names: ReactElement[] = [];
  const buttons = marks.map((mark) => {
    const size = markSize(mark.kind);
    const left = Math.round(mark.x * MAP_SCALE - size / 2);
    const top = Math.round(mark.y * MAP_SCALE - size / 2);
    const colour = mark.kind === "party" ? partyColourFor(mark.key, mark.place) : undefined;
    if (colour === null) {
      return null;
    }
    const isNamed = named.includes(mark.key);
    if (isNamed) {
      const box = nameBox({ key: mark.key, name: mark.name, x: mark.x, y: mark.y, size });
      names.push(
        <span key={`${mark.key}-name`} className="ot-worldmap__name ot-pixel-small" data-worldmap="name" aria-hidden="true" style={{ left: logical(box.left), top: logical(box.top) }}>
          {mark.name}
        </span>,
      );
    }
    return (
      <Mark
        key={mark.key}
        kind={mark.kind}
        left={left}
        top={top}
        width={size}
        height={size}
        name={mark.name}
        colour={colour}
        tabbable={isNamed}
        hover={(on) => {
          map.hoverMark(on ? mark.key : null);
          onTip(on ? map.lineOf(mark.key) : null);
        }}
        focus={(on) => {
          map.focusMark(on ? mark.key : null);
          onTip(on ? map.lineOf(mark.key) : null);
        }}
        onPress={() => map.pinPlace(mark.key)}
      >
        {mark.kind === "shrine" ? <span className="ot-worldmap__core" aria-hidden="true" /> : null}
      </Mark>
    );
  });
  return (
    <>
      {buttons}
      {names}
    </>
  );
}

const MarksLayer = memo(MarksLayerView);

/**
 * The plaque of an edge arrow: a small square 10 px in from the view's edge with the arrow turned to the way it
 * points, and its words beside it.
 *
 * @param props The `mark` and the `view` it sits in, and the words.
 * @returns The plaque.
 */
function EdgePlaque({ mark, viewWidth, words }: { readonly mark: EdgeMark; readonly viewWidth: number; readonly words: string }): ReactElement {
  const half = MARK_SIZES.plaque / 2;
  const onRight = mark.x > viewWidth / 2;
  return (
    <div className="ot-worldmap__edge" data-worldmap="edge" role="img" aria-label={words} style={{ left: logical(Math.round(mark.x - half)), top: logical(Math.round(mark.y - half)), width: logical(MARK_SIZES.plaque), height: logical(MARK_SIZES.plaque) }}>
      <span className="ot-worldmap__arrow" aria-hidden="true" style={{ transform: `rotate(${Math.round(((mark.angle * 180) / Math.PI + 90) * 10) / 10}deg)` }} />
      <span className={`ot-worldmap__edge-words ${onRight ? "ot-worldmap__edge-words--left" : ""}`} aria-hidden="true">
        {words}
      </span>
    </div>
  );
}

/**
 * Whether a box lies at least partly inside the view.
 *
 * @param left The box's left in view pixels.
 * @param top The box's top.
 * @param width The box's width.
 * @param height The box's height.
 * @param view The view's size.
 * @returns True when any of it shows.
 */
function shows(left: number, top: number, width: number, height: number, view: { readonly width: number; readonly height: number }): boolean {
  return left + width > 0 && top + height > 0 && left < view.width && top < view.height;
}

/**
 * The view: the canvas the store paints, the marks over it, the loader while chunks are owed, the tip of the mark
 * named, and, in the window, the Places button in its corner and Centre and Clear pin over its top right.
 *
 * @param props The `map`, its `snapshot`, the `controls` to lay over the view (null where the page draws them
 *   elsewhere: the phone layout's footer, the float's strip), whether the Places button stands in the view's `corner`
 *   (the float's strip holds it instead), `panelTop`, where the Places panel starts, and `pinching`, set while two
 *   fingers are down, when a press neither drags nor drops a pin.
 * @returns The view.
 */
function MapView({ map, snapshot, controls, corner, panelTop, pinching }: { readonly map: WorldMapControl; readonly snapshot: WorldMapSnapshot; readonly controls: ReactNode | null; readonly corner: boolean; readonly panelTop: number | undefined; readonly pinching: MutableRefObject<boolean> }): ReactElement {
  const canvas = useRef<HTMLCanvasElement>(null);
  const press = useRef<{ readonly id: number; x: number; y: number; moved: boolean; travelled: number } | null>(null);
  const [tip, setTip] = useState<string | null>(null);
  const { view, origin } = snapshot;
  const travel = useContext(TravelContext);
  const travelling = useTravel(travel);
  const offered = travel !== undefined && travelling !== null && travelling.offer !== null ? travelling : null;

  useLayoutEffect(() => {
    const context = canvas.current?.getContext("2d") ?? null;
    if (context === null) {
      return undefined;
    }
    const paint = (): void => {
      const now = map.getSnapshot();
      paintView(context, map.source, now.view, now.origin, map.pictureSize);
    };
    paint();
    return map.onDraw(paint);
  }, [map, view.width, view.height]);

  /**
   * The ratio of a device pixel run to a logical pixel along the view.
   *
   * @param element The view's element.
   * @returns Device pixels a logical pixel.
   */
  const ratioOf = (element: HTMLElement): number => {
    const width = element.getBoundingClientRect().width;
    return width > 0 ? width / view.width : 1;
  };

  /**
   * Starts a press: a drag if it moves, a pin if it does not.
   *
   * @param event The press.
   */
  const onPointerDown = (event: PointerEvent<HTMLDivElement>): void => {
    if (event.button !== 0 || pinching.current) {
      return;
    }
    event.currentTarget.setPointerCapture?.(event.pointerId);
    press.current = { id: event.pointerId, x: event.clientX, y: event.clientY, moved: false, travelled: 0 };
  };

  /**
   * Drags the view by whole logical pixels once the press has moved past the click slop.
   *
   * @param event The move.
   */
  const onPointerMove = (event: PointerEvent<HTMLDivElement>): void => {
    const held = press.current;
    if (held === null || held.id !== event.pointerId) {
      return;
    }
    if (pinching.current) {
      press.current = null;
      return;
    }
    const ratio = ratioOf(event.currentTarget);
    const dx = Math.trunc((event.clientX - held.x) / ratio);
    const dy = Math.trunc((event.clientY - held.y) / ratio);
    held.travelled = Math.max(held.travelled, Math.hypot((event.clientX - held.x) / ratio, (event.clientY - held.y) / ratio));
    if (!held.moved && held.travelled <= CLICK_SLOP) {
      return;
    }
    held.moved = true;
    if (dx !== 0 || dy !== 0) {
      held.x += dx * ratio;
      held.y += dy * ratio;
      map.pan(dx, dy);
    }
  };

  /**
   * Ends a press, dropping the pin where it landed when it was not a drag.
   *
   * @param event The release.
   */
  const onPointerUp = (event: PointerEvent<HTMLDivElement>): void => {
    const held = press.current;
    press.current = null;
    if (held === null || held.id !== event.pointerId || held.moved) {
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const ratio = ratioOf(event.currentTarget);
    map.placePin(tilePoint(map.getSnapshot().origin, (event.clientX - rect.left) / ratio, (event.clientY - rect.top) / ratio));
  };

  /**
   * Moves the view with the arrow keys while it has focus, which then never walk the character.
   *
   * @param event The key press.
   */
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const moves: Readonly<Record<string, readonly [number, number]>> = { ArrowLeft: [KEY_PAN, 0], ArrowRight: [-KEY_PAN, 0], ArrowUp: [0, KEY_PAN], ArrowDown: [0, -KEY_PAN] };
    const move = moves[event.key];
    if (move === undefined || event.target !== event.currentTarget) {
      return;
    }
    event.preventDefault();
    map.pan(move[0], move[1]);
  };

  const marks: ReactElement[] = [];
  const box = (point: TilePoint, width: number, height: number, tip: boolean): { left: number; top: number } => {
    const at = pixelPoint(origin, point);
    return { left: Math.round(at.x - width / 2), top: Math.round(tip ? at.y - height : at.y - height / 2) };
  };
  const plain = (on: boolean, words: string): void => setTip(on ? words : null);
  if (snapshot.pin !== null && snapshot.pinLine !== null) {
    const { left, top } = box(snapshot.pin, MARK_SIZES.pinWidth, MARK_SIZES.pinHeight, true);
    if (shows(left, top, MARK_SIZES.pinWidth, MARK_SIZES.pinHeight, view)) {
      const line = snapshot.pinLine;
      marks.push(<Mark key="pin" kind="pin" left={left} top={top} width={MARK_SIZES.pinWidth} height={MARK_SIZES.pinHeight} name={line} tabbable hover={(on) => plain(on, line)} focus={(on) => plain(on, line)} />);
    }
  }
  if (snapshot.you !== null && snapshot.youEdge === null) {
    const { left, top } = box(snapshot.you, MARK_SIZES.you, MARK_SIZES.you, false);
    const words = say("worldMap.you");
    marks.push(
      <Mark key="you" kind="you" left={left} top={top} width={MARK_SIZES.you} height={MARK_SIZES.you} name={words} tabbable hover={(on) => plain(on, words)} focus={(on) => plain(on, words)}>
        <span className="ot-worldmap__arrow" aria-hidden="true" style={{ transform: `rotate(${Math.round(((snapshot.you.heading * 180) / Math.PI + 90) * 10) / 10}deg)` }} />
      </Mark>,
    );
  }

  return (
    <div className="ot-worldmap__view" data-worldmap="view" tabIndex={0} role="group" aria-label={snapshot.mapLabel} style={{ width: logical(view.width), height: logical(view.height) }} onMouseDown={keepFocus} onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={() => (press.current = null)} onKeyDown={onKeyDown}>
      <canvas ref={canvas} className="ot-worldmap__canvas" data-worldmap="canvas" role="img" aria-label={snapshot.mapLabel} width={view.width} height={view.height} style={{ width: logical(view.width), height: logical(view.height) }} />
      <div className="ot-worldmap__marks" data-worldmap="marks" style={{ transform: `translate(${logical(-origin.left)}, ${logical(-origin.top)})` }}>
        {offered === null || travel === undefined ? <MarksLayer marks={snapshot.marks} named={snapshot.named} map={map} onTip={setTip} /> : <TravelMarks snapshot={offered} travel={travel} />}
      </div>
      {marks}
      {snapshot.youEdge === null ? null : <EdgePlaque mark={snapshot.youEdge} viewWidth={view.width} words={say("worldMap.youAreHere")} />}
      <span className="ot-worldmap__sr" role="status">
        {snapshot.pin === null ? "" : say("worldMap.pinSet")}
      </span>
      {snapshot.drawing ? (
        <div className="ot-worldmap__loader" data-worldmap="loader" role="status">
          <Glyph name="spinner" />
          <span className="ot-worldmap__sr">{say("worldMap.drawing")}</span>
        </div>
      ) : null}
      {tip === null ? null : (
        <div className="ot-worldmap__tip" data-worldmap="tip" aria-hidden="true">
          {tip}
        </div>
      )}
      {snapshot.panelOpen && offered === null ? <div className="ot-worldmap__backdrop" data-worldmap="backdrop" onPointerDown={stopPress} onMouseDown={keepFocus} onClick={() => map.closePanel()} /> : null}
      {snapshot.panelOpen && offered === null ? <PlacesPanel map={map} snapshot={snapshot} top={panelTop} /> : null}
      {offered !== null && travel !== undefined ? <TravelPanel snapshot={offered} travel={travel} view={view} mapName={snapshot.title} /> : null}
      {corner ? (
        <div className="ot-worldmap__corner" data-worldmap="corner" onPointerDown={stopPress} onMouseDown={keepFocus}>
          {offered === null ? (
            <Button selected={snapshot.panelOpen} onClick={() => map.togglePanel()}>
              {say("worldMap.placesButton")}
            </Button>
          ) : null}
        </div>
      ) : null}
      {controls === null ? null : (
        <div className="ot-worldmap__over" onPointerDown={stopPress}>
          {controls}
        </div>
      )}
    </div>
  );
}

/**
 * The Nearby level: the minimap's field across the view, its marks, the names of other players and the legend of the
 * kinds in view under it, as the Nearby window drew them (ADR-100 decision 44). Nothing here pans or pins: the field is
 * always on the player.
 *
 * @param props The `view`'s size, the `field` the store holds for the Map, the `minimap` it is drawn from, the
 *   `controls` to lay over the view (null where the page draws them elsewhere) and the legend's `strip`, 0 where the
 *   legend is left out (a float too small for it).
 * @returns The view.
 */
function NearbyView({ view, field, minimap, controls, strip }: { readonly view: Size; readonly field: MinimapField; readonly minimap: HudMinimap; readonly controls: ReactNode | null; readonly strip: number }): ReactElement {
  return (
    <div className="ot-worldmap__view ot-worldmap__view--nearby" data-worldmap="view" data-nearby="view" role="group" aria-label={say("minimap.field")} style={{ width: logical(view.width), height: logical(view.height) }} onMouseDown={keepFocus}>
      <div className="ot-world-map__nearby-field ot-worldmap__field" data-nearby="field" style={{ left: logical(Math.floor((view.width - field.size.width) / 2)), width: logical(field.size.width), height: logical(field.size.height) }}>
        <FieldCanvas field={field} slot="nearby" position={minimap.position} onMove={minimap.onMove} ground={minimap.ground} others={minimap.others} label={say("minimap.field")} />
        <Labels field={field} />
        <OtherNames field={field} position={minimap.position} onMove={minimap.onMove} others={minimap.others} />
      </div>
      {strip === 0 ? null : (
        <div className="ot-worldmap__legend" data-nearby="legend" style={{ top: logical(field.size.height + LEGEND_GAP), height: logical(Math.max(0, strip - LEGEND_GAP)) }}>
          <Legend source={minimap.source} others={minimap.others} onMove={minimap.onMove} className="ot-worldmap__legend-list" />
        </div>
      )}
      {controls === null ? null : (
        <div className="ot-worldmap__over" onPointerDown={stopPress}>
          {controls}
        </div>
      )}
    </div>
  );
}

/**
 * Known places, the panel over the map: each place found, nearest first, grouped by kind under small
 * headings, with a filter field above twelve places. It draws only the rows in view and the one with the keyboard's
 * place, so its cost does not grow with the places. A click, a tap, Space or Enter on a row pins the place; the arrow
 * keys, Home and End move between rows across the groups and never walk the character, and a row with focus names its
 * mark on the map.
 *
 * @param props The `map`, its `snapshot` and `top`, where the panel starts in the view (under the Places button
 *   unless given).
 * @returns The panel.
 */
function PlacesPanel({ map, snapshot, top }: { readonly map: WorldMapControl; readonly snapshot: WorldMapSnapshot; readonly top: number | undefined }): ReactElement {
  const model: PanelModel<KnownPlace> | null = snapshot.panel;
  const filterShown = showsFilter(snapshot.count);
  const box = panelBox(snapshot.view, filterShown, top);
  const [kept, setKept] = useState<PanelScroll>({ top: 0, filter: snapshot.filter });
  const scrollTop = scrollUnder(kept, snapshot.filter);
  const setScrollTop = (top: number): void => setKept({ top, filter: snapshot.filter });
  const [active, setActive] = useState<string | null>(null);
  const list = useRef<HTMLDivElement>(null);
  const wanted = useRef<string | null>(null);
  const metrics = KIT_METRICS.list;
  const activeKey = model === null ? null : active !== null && model.index.has(active) ? active : (model.order[0] ?? null);

  /**
   * Moves keyboard focus to a row if it is drawn.
   *
   * @param key The place's key.
   * @returns True when the row took focus.
   */
  const focusRow = (key: string): boolean => {
    const rows = list.current?.querySelectorAll<HTMLElement>("[data-place]") ?? [];
    for (const row of rows) {
      if (row.dataset["place"] === key) {
        (row.querySelector("[data-kit='list-row']") as HTMLElement | null)?.focus();
        return true;
      }
    }
    return false;
  };

  useLayoutEffect(() => {
    if (list.current !== null && scrollTop === 0) {
      list.current.scrollTop = 0;
    }
  }, [snapshot.filter]);

  useLayoutEffect(() => {
    const key = wanted.current;
    if (key !== null && focusRow(key)) {
      wanted.current = null;
    }
  });

  /**
   * Moves focus between rows across the groups, scrolling the row into view.
   *
   * @param event The key press on a row.
   */
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (model === null) {
      return;
    }
    const from = activeKey === null ? -1 : model.order.indexOf(activeKey);
    const to = choiceForKey(event.key, model.order.map(() => false), from, "vertical");
    if (to === null || to < 0) {
      return;
    }
    event.preventDefault();
    const key = model.order[to] as string;
    const next = scrollFor(model, key, scrollTop, box.list);
    setActive(key);
    setScrollTop(next);
    if (list.current !== null) {
      list.current.scrollTop = next;
    }
    wanted.current = key;
    if (focusRow(key)) {
      wanted.current = null;
    }
  };

  const drawn = model === null ? [] : [...visibleItems(model, scrollTop, box.list)];
  const activeRow = model === null || activeKey === null ? undefined : model.index.get(activeKey);
  if (activeRow !== undefined && !drawn.includes(activeRow)) {
    drawn.push(activeRow);
  }
  const matching = model === null ? 0 : model.matching;
  const note = snapshot.count === 0 || model === null ? say("worldMap.noneKnown") : matching === 0 ? say("worldMap.noMatch") : null;

  return (
    <div className="ot-worldmap__places" data-worldmap="places" role="group" aria-label={say("worldMap.places")} style={{ left: logical(box.left), top: logical(box.top), width: logical(box.width), maxHeight: logical(box.height) }} onPointerDown={stopPress} onMouseDown={(event) => event.stopPropagation()}>
      <div className="ot-worldmap__heading" aria-hidden="true" style={{ height: logical(metrics.heading), paddingTop: logical(metrics.headingTop) }}>
        <span>{say("worldMap.places")}</span>
        <span className="ot-worldmap__count">{snapshot.count}</span>
      </div>
      {filterShown ? (
        <div className="ot-worldmap__filter" data-worldmap="filter" style={{ height: logical(KIT_METRICS.field.boxHeight + 4) }}>
          <SearchField label={say("worldMap.filter")} placeholder={say("worldMap.filter")} value={snapshot.filter} onChange={(text) => map.setFilter(text)} maxLength={FILTER_LENGTH} count={snapshot.filter === "" ? null : { text: say("worldMap.filterCount", { shown: matching, total: snapshot.count }), none: matching === 0 }} clearLabel={say("worldMap.filterClear")} />
        </div>
      ) : null}
      <div ref={list} role="listbox" aria-label={say("worldMap.places")} className="ot-worldmap__list ot-scroll" data-worldmap="list" style={{ maxHeight: logical(box.list) }} onKeyDown={onKeyDown} onScroll={(event) => setScrollTop(event.currentTarget.scrollTop)}>
        <div className="ot-worldmap__rows" style={{ height: logical(model === null ? 0 : model.height) }}>
          {drawn.map((item) =>
            item.type === "heading" ? (
              <div key={`heading-${item.kind}`} role="presentation" className="ot-worldmap__group" data-worldmap="group" style={{ top: logical(item.top), height: logical(item.height) }}>
                <span>{say(`worldMap.kind.${item.kind as PlaceKind}`)}</span>
                <span className="ot-worldmap__count">{item.count}</span>
              </div>
            ) : (
              <div key={item.entry.key} role="presentation" className="ot-worldmap__row" data-place={item.entry.key} style={{ top: logical(item.top), height: logical(ROW_HEIGHT) }} onFocus={() => {
                setActive(item.entry.key);
                map.focusMark(item.entry.key);
              }} onBlur={() => map.focusMark(null)}>
                <ListRow
                  label={item.entry.name}
                  sub={item.entry.where === "away" ? say("worldMap.elsewhere") : item.entry.distance === 0 ? say("worldMap.here") : distanceWords(item.entry.distance, item.entry.direction)}
                  {...swatchLead(item.entry)}
                  selected={item.entry.where !== "away" && snapshot.pin !== null && snapshot.pin.x === item.entry.x && snapshot.pin.y === item.entry.y}
                  tabStop={item.entry.key === activeKey}
                  onPick={() => map.pinPlace(item.entry.key)}
                  onActivate={() => map.pinPlace(item.entry.key)}
                />
              </div>
            ),
          )}
        </div>
        {note === null ? null : (
          <div className="ot-worldmap__note" data-worldmap="note" role="status">
            {note}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * The zoom buttons (decision 41): zoom out and zoom in, each disabled at its end of the ladder.
 *
 * @param props The `map` and its `snapshot`.
 * @returns The two buttons.
 */
export function ZoomControls({ map, snapshot }: { readonly map: WorldMapControl; readonly snapshot: WorldMapSnapshot }): ReactElement {
  return (
    <>
      <span onMouseDown={keepFocus}>
        <GlyphButton glyph="minus" label={say("worldMap.zoomOut")} size={16} disabled={!snapshot.canZoomOut} onClick={() => map.zoomOut()} data={{ "data-worldmap": "zoom-out" }} />
      </span>
      <span onMouseDown={keepFocus}>
        <GlyphButton glyph="plus" label={say("worldMap.zoomIn")} size={16} disabled={!snapshot.canZoomIn} onClick={() => map.zoomIn()} data={{ "data-worldmap": "zoom-in" }} />
      </span>
    </>
  );
}

/**
 * Centre and Clear pin: Centre is lit while the view has been dragged and does nothing while it follows, Clear pin
 * does nothing without a pin.
 *
 * @param props The `map` and its `snapshot`.
 * @returns The two buttons.
 */
export function MapControls({ map, snapshot }: { readonly map: WorldMapControl; readonly snapshot: WorldMapSnapshot }): ReactElement {
  return (
    <>
      <span onMouseDown={keepFocus}>
        <Button disabled={snapshot.following} selected={!snapshot.following} onClick={() => map.centre()}>
          {say("worldMap.centre")}
        </Button>
      </span>
      <span onMouseDown={keepFocus}>
        <Button disabled={snapshot.pin === null} onClick={() => map.clearPin()}>
          {say("worldMap.clearPin")}
        </Button>
      </span>
    </>
  );
}

/**
 * Steps the ladder from the wheel over the window and from a two finger pinch (decision 41). The wheel is taken whole,
 * so it never reaches the page's world (the window catches the pointer over its whole box), except over the places
 * list, which scrolls; scrolled up zooms in, down zooms out. A pinch is two pointers on the window: fingers a third
 * further apart than they began are one step in, a quarter closer one step out, and each step begins the next.
 *
 * @param root The window's element.
 * @param map The store the steps go to.
 * @param pinching Set while two pointers are down.
 */
export function useZoomInput(root: RefObject<HTMLElement | null>, map: WorldMapControl, pinching: MutableRefObject<boolean>): void {
  useEffect(() => {
    const element = root.current;
    if (element === null) {
      return undefined;
    }
    let held = 0;
    let start = 0;
    const points = new Map<number, { x: number; y: number }>();
    /**
     * The distance between the two pointers down, or 0 while fewer are.
     * @returns The distance in pixels.
     */
    const apart = (): number => {
      const [a, b] = [...points.values()];
      return a === undefined || b === undefined ? 0 : Math.hypot(a.x - b.x, a.y - b.y);
    };
    /**
     * Steps the ladder a number of times, in when positive, out when negative.
     * @param direction The signed number of steps.
     */
    const step = (direction: number): void => {
      for (let i = 0; i < Math.abs(direction); i += 1) {
        if (direction > 0) {
          map.zoomIn();
        } else {
          map.zoomOut();
        }
      }
    };
    /**
     * Takes a wheel event whole, except over the places list, and steps the ladder by what it adds up to.
     * @param event The wheel event.
     */
    const onWheel = (event: WheelEvent): void => {
      const target = event.target as { readonly closest?: (selector: string) => unknown } | null;
      if (typeof target?.closest === "function" && target.closest("[data-worldmap='places']") !== null) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      const taken = wheelSteps(held, event.deltaY, event.deltaMode);
      held = taken.rest;
      step(taken.steps);
    };
    /**
     * Keeps a pointer going down, and begins a pinch when it is the second.
     * @param event The pointer event.
     */
    const onDown = (event: globalThis.PointerEvent): void => {
      points.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (points.size === 2) {
        start = apart();
        pinching.current = true;
      }
    };
    /**
     * Follows a held pointer, and steps the ladder when a pinch has gone far enough.
     * @param event The pointer event.
     */
    const onMove = (event: globalThis.PointerEvent): void => {
      if (!points.has(event.pointerId)) {
        return;
      }
      points.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (points.size === 2) {
        const now = apart();
        const taken = pinchStep(start, now);
        if (taken !== 0) {
          start = now;
          step(taken);
        }
      }
    };
    /**
     * Lets a pointer go, and ends the pinch when fewer than two remain.
     * @param event The pointer event.
     */
    const onUp = (event: globalThis.PointerEvent): void => {
      points.delete(event.pointerId);
      if (points.size < 2) {
        pinching.current = false;
      }
    };
    element.addEventListener("wheel", onWheel, { passive: false });
    element.addEventListener("pointerdown", onDown, true);
    element.addEventListener("pointermove", onMove, true);
    element.addEventListener("pointerup", onUp, true);
    element.addEventListener("pointercancel", onUp, true);
    return () => {
      element.removeEventListener("wheel", onWheel);
      element.removeEventListener("pointerdown", onDown, true);
      element.removeEventListener("pointermove", onMove, true);
      element.removeEventListener("pointerup", onUp, true);
      element.removeEventListener("pointercancel", onUp, true);
    };
  }, [root, map, pinching]);
}

/**
 * What a presentation of the Map needs to know of its levels: which level is drawn and the Nearby field the minimap's
 * store holds for it. At Nearby it asks the minimap's store for the field the view leaves under the legend and has it
 * once the store does; at Region it asks for none, which frees the `nearby` picture, once Region has its first chunk:
 * until then the Nearby view it left stays drawn. Zooming in to a Nearby field not made yet keeps Region drawn the same
 * way (P2.12-T32). The window and the float both read their levels through it.
 *
 * @param view The view's size in logical pixels.
 * @param strip The strip the legend takes under the Nearby field, 0 for none.
 * @param snapshot The store's snapshot.
 * @param minimap The minimap the Nearby level is drawn from, if the page has one.
 * @returns The level drawn (null while neither level has a picture) and the Nearby field held, or null.
 */
export function useMapLevels(view: Size, strip: number, snapshot: WorldMapSnapshot, minimap: HudMinimap | undefined): { readonly shown: MapLevel | null; readonly held: MinimapField | null } {
  /**
   * Reads the minimap's Nearby field for the Map, or null.
   * @returns The field.
   */
  const readField = (): MinimapField | null => minimap?.source.getSnapshot().map ?? null;
  const held = useSyncExternalStore(minimap?.source.subscribe ?? noSubscription, readField, readField);
  const previous = useRef<MapLevel | null>(null);
  const nearby = minimap !== undefined && (snapshot.level === "nearby" || (previous.current === "nearby" && !snapshot.regionReady));
  const wanted = nearby ? nearbyField(view, strip, minimapScale(snapshot.step, true)) : null;
  const width = wanted?.width ?? 0;
  const height = wanted?.height ?? 0;
  useLayoutEffect(() => {
    minimap?.onMapField?.(wanted === null || width === 0 || height === 0 ? null : { width, height });
  }, [minimap, nearby, width, height]);
  useLayoutEffect(() => () => minimap?.onMapField?.(null), [minimap]);
  const ready = held !== null && wanted !== null && held.size.width === width && held.size.height === height && held.scale === minimapScale(snapshot.step, true);
  const shown = shownLevel(snapshot.level, { regionReady: snapshot.regionReady, nearbyReady: ready }, previous.current) ?? (snapshot.level === "region" ? "region" : null);
  useLayoutEffect(() => {
    previous.current = shown;
  });
  return { shown, held };
}

/**
 * The level's view in a presentation's body: Region's map, Nearby's field, or an empty view while neither has a
 * picture.
 *
 * @param props The `shown` level and the Nearby field `held` (from `useMapLevels`), the `view`'s size, the legend's
 *   `strip`, the `store` and its `snapshot`, the `minimap`, the `controls` to lay over the view (null where the page
 *   draws them elsewhere), whether Places has a `corner` button, where its panel starts (`panelTop`) and `pinching`.
 * @returns The view.
 */
export function LevelView({ shown, held, view, strip, store, snapshot, minimap, controls, corner, panelTop, pinching }: { readonly shown: MapLevel | null; readonly held: MinimapField | null; readonly view: Size; readonly strip: number; readonly store: WorldMapControl; readonly snapshot: WorldMapSnapshot; readonly minimap: HudMinimap | undefined; readonly controls: ReactNode | null; readonly corner: boolean; readonly panelTop: number | undefined; readonly pinching: MutableRefObject<boolean> }): ReactElement {
  if (shown === "region") {
    return <MapView map={store} snapshot={snapshot} controls={controls} corner={corner} panelTop={panelTop} pinching={pinching} />;
  }
  if (shown === "nearby" && minimap !== undefined && held !== null) {
    return <NearbyView view={view} field={held} minimap={minimap} controls={controls} strip={strip} />;
  }
  return <div className="ot-worldmap__view ot-worldmap__view--nearby" data-worldmap="view" style={{ width: logical(view.width), height: logical(view.height) }} />;
}

/**
 * The Float button of the title bar (P2.12-T23) and, where the browser can open one, the Pop out button (P2.12-T18): the
 * Map shown as the float or in a window of its own, in the title bar beside Close.
 *
 * @param props The `store` they are asked of and the `popout` that opens the window, if the page has one.
 * @returns The buttons.
 */
function ShowButtons({ store, popout }: { readonly store: WorldMapControl; readonly popout: MapPopout | undefined }): ReactElement {
  return (
    <>
      <GlyphButton glyph="float" label={say("worldMap.float")} size={KIT_METRICS.iconButton.height} keepFocus onClick={() => store.float()} data={{ "data-worldmap": "float-button" }} />
      {popout === undefined || !popout.available() ? null : <PopOutButton store={store} popout={popout} size={KIT_METRICS.iconButton.height} name="popout-button" />}
    </>
  );
}

/**
 * The window while it is open: the frame, the zoom input over its whole box, and the level's view in its body.
 *
 * @param props The `layout`, the `store`, its `snapshot`, the `minimap` Nearby is drawn from, if any, and the `popout`
 *   that opens the window Pop out asks for, if the page has one.
 * @returns The window.
 */
function OpenWindow({ layout, store, snapshot, minimap, popout }: { readonly layout: WorldMapLayout; readonly store: WorldMapControl; readonly snapshot: WorldMapSnapshot; readonly minimap: HudMinimap | undefined; readonly popout: MapPopout | undefined }): ReactElement {
  const root = useRef<HTMLDivElement>(null);
  const pinching = useRef(false);
  useZoomInput(root, store, pinching);
  const travel = useContext(TravelContext);
  const strip = legendStrip(layout.view.width, legendWords());
  const { shown, held } = useMapLevels(layout.view, strip, snapshot, minimap);
  const travelling = useTravel(travel);
  const controls = (
    <>
      {travel === undefined ? null : (
        <span onMouseDown={keepFocus} data-worldmap="travel-button">
          <Button selected={travelling?.offer != null} onClick={() => travel.ask()}>
            {say("travel.button")}
          </Button>
        </span>
      )}
      <ZoomControls map={store} snapshot={snapshot} />
      {shown === "region" ? <MapControls map={store} snapshot={snapshot} /> : null}
    </>
  );
  return (
    <div ref={root} className="ot-worldmap" data-worldmap="window" data-layout={layout.phone ? "phone" : "window"} data-level={snapshot.level} style={{ left: logical(layout.left), top: logical(layout.top) }}>
      <Window title={travelHeading(travelling) ?? snapshot.heading} width={layout.window.width} height={layout.window.height} padding={layout.padding} onClose={() => store.close()} actions={layout.phone ? undefined : <ShowButtons store={store} popout={popout} />} footer={layout.phone ? <div className="ot-worldmap__footer">{controls}</div> : undefined}>
        <div className="ot-worldmap__body" data-worldmap="body">
          <LevelView shown={shown} held={held} view={layout.view} strip={strip} store={store} snapshot={snapshot} minimap={minimap} controls={layout.phone ? null : controls} corner panelTop={undefined} pinching={pinching} />
          {travelling === null ? null : <TravelNote snapshot={travelling} />}
        </div>
      </Window>
    </div>
  );
}

/**
 * A subscription that never fires, for a Map with no minimap to read a Nearby field from.
 *
 * @returns A function that stops listening, which does nothing.
 */
function noSubscription(): () => void {
  return () => undefined;
}

/**
 * The window, while the store says it is open and shown as the window (the float, P2.12-T23, is the other way the Map
 * is shown; on the phone layout the window alone is).
 *
 * @param props The `area` it is laid out on, the `map` it reads and acts on, the `minimap` its Nearby level is
 *   drawn from, which a page without one omits (the Map then has Region alone), and `size`, the size the player has
 *   given the window, which the window frame supplies and which is held to the crop's limit (the default size when
 *   omitted).
 * @returns The window, or nothing while shut.
 */
export function WorldMapWindow({ area, map, minimap, size }: { readonly area: HudArea; readonly map: HudWorldMap; readonly minimap?: HudMinimap | undefined; readonly size?: Size | null | undefined }): ReactElement | null {
  const store = map.store;
  const snapshot = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
  const layout = worldMapLayout(area, size ?? null);
  const mine = snapshot.presentation === "window";
  useLayoutEffect(() => {
    store.setPhone(layout.phone);
  }, [store, layout.phone]);
  useLayoutEffect(() => {
    if (mine) {
      store.setView(layout.view);
    }
  }, [store, mine, layout.view.width, layout.view.height]);
  if (!snapshot.open || !snapshot.ready || !mine) {
    return null;
  }
  return (
    <TravelContext.Provider value={map.travel}>
      <OpenWindow layout={layout} store={store} snapshot={snapshot} minimap={minimap} popout={map.popout} />
    </TravelContext.Provider>
  );
}
